import { MovieClip, View } from '@play-co/timestep-core/ui';
import { ButtonScaleViewOpts } from 'src/lib/ui/components/ButtonScaleView';
import * as match2TournamentGetters from 'src/replicant/match2/tournament/match2-tournament.getters';
import StateObserver, { user } from 'src/StateObserver';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import {
  TournamentType,
  match2TournamentRuleset,
} from 'src/replicant/match2/tournament/match2-tournament.ruleset';
import { getTournament } from './match2-tournament.utils';
import { playMovieClip } from 'src/game/animation/animation.util';
import { AssetGroup } from '@play-co/timestep-core';
import { assertNever } from 'src/replicant/utils';
import { FeatureButton } from 'src/game/components/FeatureButton';
import { spineLoader } from '@play-co/timestep-spine';
import features from 'src/replicant/ruleset/features';
import { playSpineAnimationSequence } from 'src/lib/ui/utils/spine';

const assets = {
  star: {
    movieclip: {
      url: `assets/ui/match2/tournament/star_button`,
      loadMethod: MovieClip.animationLoader,
    },
  },
  streak: {
    movieclip: {
      url: `assets/ui/match2/tournament/streak_button`,
      loadMethod: MovieClip.animationLoader,
    },
  },
  streakRedesign: {
    spine: {
      url: 'assets/ui/match2/tournament/flyinghigh/button/spine.plane_HUDicon',
      loadMethod: spineLoader,
    },
  },
  panelBg: 'assets/ui/shared/frames/timer_bg_white.png',
};

type AnimationState = 'star' | 'streak' | 'streakRedesign';

export class Match2TournamentButtonAnimated extends FeatureButton<AnimationState> {
  private readonly text: LangBitmapFontTextView;
  private type: TournamentType = 'star';

  public override getElementsToApplyFilter() {
    const elements: View[] = [this.text];
    if (this.labelPanel) elements.push(this.labelPanel);
    if (this.movieclip) elements.push(this.movieclip);
    if (this.spine) elements.push(this.spine);
    return elements;
  }

  constructor(opts: ButtonScaleViewOpts) {
    super({
      width: 146,
      height: 160,
      pressedOffsetY: 5,
      labelPanel: {
        background: {
          image: assets.panelBg,
        },
        labelColor: '#783B3D',
        labelOffsetY: -3,
      },
      ...opts,
    });

    this.animationStateMap = {
      star: {
        type: 'movieclip',
        assetGroup: new AssetGroup([
          ...Object.values(assets.star),
          assets.panelBg,
        ]),
        url: assets.star.movieclip.url,
        sequence: ['star_spawn', 'star_loop'],
        scale: 0.65,
        onEnter: this.update.bind(this),
      },
      streak: {
        type: 'movieclip',
        assetGroup: new AssetGroup([
          ...Object.values(assets.streak),
          assets.panelBg,
        ]),
        url: assets.streak.movieclip.url,
        sequence: ['rocket_icon_spawn', 'rocket_icon_loop'],
        scale: 0.65,
        onEnter: this.update.bind(this),
      },
      streakRedesign: {
        type: 'spine',
        assetGroup: new AssetGroup([
          ...Object.values(assets.streakRedesign),
          assets.panelBg,
        ]),
        url: assets.streakRedesign.spine.url,
        sequence: ['idle'],
        scale: 0.65,
        offsetY: 5,
        onEnter: this.update.bind(this),
      },
    };

    this.text = new LangBitmapFontTextView({
      superview: this.container,
      x: this.style.width * 0.5 - 3,
      y: 105,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 20,
      font: 'Title',
      width: this.style.width,
      height: 40,
      wordWrap: true,
    });

    this.setNotification({
      condition: (user, now) =>
        !this.isLocked &&
        match2TournamentGetters.canPlay(user, now) &&
        (match2TournamentGetters.getUnclaimedEvents(user).length > 0 ||
          match2TournamentGetters.getCurrentEvent(user, now) === null),
      positioning: { align: 'right' },
    });

    this.update();
  }

  public update() {
    const user = StateObserver.getState().user;
    const now = StateObserver.now();

    let unclaimedEvents = match2TournamentGetters.getUnclaimedEvents(user);

    if (!unclaimedEvents.length) {
      unclaimedEvents = match2TournamentGetters.getPastUnclaimedEvents(
        user,
        now,
      );
    }

    // Prioritize unclaimed events, if there are any - Find past events with unclaimed rewards, if they hasn't been finished yet
    if (unclaimedEvents.length > 0) {
      const type = match2TournamentGetters.getStoredTournamentType(
        user,
        unclaimedEvents[0].id,
      )!;
      this.updateView(type);
      this.updateClaim(unclaimedEvents[0].id);
      return;
    }

    // Hide if user can't play any tournaments at the moment
    if (!match2TournamentGetters.canPlay(user, now)) {
      this.updateUnavailable();
      return;
    }

    // Get the current tournament event
    const event = match2TournamentGetters.getCurrentEvent(user, now);

    if (event) {
      const type = match2TournamentGetters.getStoredTournamentType(
        user,
        event.id,
      )!;
      const currentEvent = getTournament()?.getCached();
      const currentRank = currentEvent?.currentRank;
      this.updateView(type);
      if (!currentEvent) {
        this.updateLoading();
      } else if (currentRank) {
        this.updateOngoing(event.end, currentRank);
      } else {
        this.updateAvailable();
      }
    } else {
      const period = match2TournamentGetters.getTournamentPeriod(user, now);
      this.updateView(period?.type);
      this.updateAvailable();
    }
  }

  private updateView(type: TournamentType = 'star') {
    this.type = type;

    if (type === 'star') {
      this.text.updateOpts({
        y: 108,
      });
      this.setAnimationState('star');
    } else {
      this.text.updateOpts({
        y: 104,
      });
      this.setAnimationState(
        features(user()).flyingHighRedesign ? 'streakRedesign' : 'streak',
      );
    }
  }

  private updateUnavailable() {
    this.hide();
  }

  private updateLoading() {
    this.show();
    const now = StateObserver.now();
    const remaining = match2TournamentGetters.getRemainingTime(user(), now);
    this.setLabelTime({ duration: remaining });
    this.text.localeText = () =>
      i18n('match2.tournament.play').toLocaleUpperCase();
  }

  private updateAvailable() {
    this.show();
    const now = StateObserver.now();
    const remaining = match2TournamentGetters.getRemainingTime(user(), now);
    this.setLabelTime({ duration: remaining });
    this.text.localeText = () =>
      i18n('match2.tournament.play').toLocaleUpperCase();
  }

  private updateOngoing(endDate = 0, position = 0) {
    this.show();
    const now = StateObserver.now();
    const remaining = endDate - now;
    this.setLabelTime({ duration: remaining });
    this.text.localeText = () =>
      i18n('match2.tournament.position', {
        position: position ? position.toString() : '?',
      }).toLocaleUpperCase();
  }

  private updateClaim(tournamentId: string) {
    this.show();
    this.setLabelTime({ duration: 0 });
    const eventData = getTournament(tournamentId)?.getCached();
    const rank = eventData?.finalRank ?? eventData?.currentRank ?? 0;
    const reward = match2TournamentRuleset.getRewardsByRank(rank);
    if (!reward) {
      this.text.localeText = () =>
        i18n('match2.tournament.position', {
          position: rank || '?',
        }).toLocaleUpperCase();
    } else this.text.localeText = () => i18n('match2.tournament.claim');
  }

  public async playHitAnimation() {
    switch (this.type) {
      case 'star':
        if (!this.movieclip) return;
        await playMovieClip(this.movieclip, {
          animation: ['star_progress', 'star_loop'],
          loopLast: true,
          restart: true,
        });
        break;
      case 'streak':
        if (this.currentAnimationState === 'streakRedesign') {
          if (!this.spine) return;
          await playSpineAnimationSequence(this.spine, {
            sequence: ['collect', 'idle'],
            loopLast: true,
            clear: true,
          });
          break;
        }
        if (!this.movieclip) return;
        await playMovieClip(this.movieclip, {
          animation: ['rocket_icon_progress', 'rocket_icon_loop'],
          loopLast: true,
          restart: true,
        });
        break;
      default:
        assertNever(this.type);
    }
  }
}
